import React from 'react';
import { Card, CardContent, Typography, Box, Stack, useTheme, IconButton, Tooltip } from '@mui/material';
import SummaryCard from './SummaryCard';
import { ReactComponent as ExportIcon } from '../assets/icons/export.svg';

interface KpiData {
  totalSpend: number;
  forecastedSpend: number;
  totalSavings: number;
  totalKubernetesCost: number; 
  creditsUsed: number;
  totalSpendChangePercentage: number | null;
  forecastedSpendChangePercentage: number | null;
  totalKubernetesCostChangePercentage: number | null;
}

interface CostOverviewProps {
  userName?: string | null;
  kpiData?: KpiData | null;
  isLoading?: boolean;
}

const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '--';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatPercentage = (value: number | undefined | null): string | undefined => {
  if (value === undefined || value === null) return undefined;
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const CostOverview: React.FC<CostOverviewProps> = ({ userName, kpiData, isLoading }) => {
  const theme = useTheme();

  // --- MODIFICATION: Added unique textureImagePath for each card ---
  const summaryCardItems = [
    {
      title: 'Total Spend',
      amount: formatCurrency(kpiData?.totalSpend),
      change: formatPercentage(kpiData?.totalSpendChangePercentage),
      changeType: kpiData?.totalSpendChangePercentage && kpiData.totalSpendChangePercentage > 0 ? 'negative' : (kpiData?.totalSpendChangePercentage && kpiData.totalSpendChangePercentage < 0 ? 'positive' : undefined),
      changeDescription: 'vs last month',
      textureImagePath: '/textures/1st-card.svg',
    },
    {
      title: 'Forecasted Spend',
      amount: formatCurrency(kpiData?.forecastedSpend),
      change: formatPercentage(kpiData?.forecastedSpendChangePercentage),
      changeType: kpiData?.forecastedSpendChangePercentage && kpiData.forecastedSpendChangePercentage > 0 ? 'negative' : undefined,
      changeDescription: 'projected increase',
      textureImagePath: '/textures/2nd-card.svg',
    },
    {
      title: 'Total Kubernetes Spend',
      amount: formatCurrency(kpiData?.totalKubernetesCost),
      change: formatPercentage(kpiData?.totalKubernetesCostChangePercentage),
      changeType: kpiData?.totalKubernetesCostChangePercentage && kpiData.totalKubernetesCostChangePercentage > 0 ? 'negative' : (kpiData?.totalKubernetesCostChangePercentage && kpiData.totalKubernetesCostChangePercentage < 0 ? 'positive' : undefined),
      changeDescription: 'vs last month',
      textureImagePath: '/textures/3rd-card.svg',
    },
    {
      title: 'Credits Used',
      amount: formatCurrency(kpiData?.creditsUsed),
      textureImagePath: '/textures/4th-card.svg',
    },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#F9FAFB',
        pb:'2px',
      }}
    >
      <CardContent sx={{
        pt: { xs: 1, md: 1},
        px: { xs: 0, md: 0 },
        py: { xs: 3, md: 1 },
        pb: 1,
        '&:last-child': {
          pb: 0
        }
      }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mt:1 }}>
          <Box>
            <Typography fontFamily={'Inter'} component="h3" gutterBottom sx={{ fontWeight: 'Bold', fontSize:'14px' , color:'rgba(51, 51, 51, 1)',mt:-2,mb: 0.3}}>
              Hi, {userName || 'there'}
            </Typography>
            <Typography  fontFamily={'Inter'} variant="body2" color="text.secondary" sx={{ fontWeight: 'Normal', fontSize:'12px'}}>
              Track and analyse your cloud spends
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: { xs: 1, sm: 0 }, alignItems: 'center',display: 'none' }}>
            <Typography fontFamily={'Inter'} variant="body2" color="text.secondary" sx={{ fontWeight: 'Normal', fontSize:'12px'}}>
              Showing data for current month
            </Typography>
            <Tooltip title="Export Report">
              <IconButton
                aria-label="Export Report"
                sx={{
                  minWidth: 0,
                  height: '40px',
                  width:'40px',
                  padding: '5px 5px',
                  borderRadius: '12px',
                  color: '#0064FF',
                  border: '1.5px solid #0064FF',
                }}
              >
              <ExportIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2), mt: 2 }}>
          {summaryCardItems.map((itemData) => (
            <Box
              key={itemData.title}
              sx={{
                flexGrow: { xs: 1, sm: 1, lg: 0 },
                flexShrink: 0,
                flexBasis: {
                  xs: '100%',
                  sm: `calc(50% - ${theme.spacing(1)})`,
                  md: `calc(25% - ${theme.spacing(1.5)})`,
                  lg: `calc(25% - ${theme.spacing(1.5)})`
                },
                minWidth: '195px',
              }}
            >
              <SummaryCard
                isLoading={isLoading}
                title={itemData.title}
                amount={itemData.amount}
                change={itemData.change}
                changeType={itemData.changeType as 'positive' | 'negative' | 'info' | undefined}
                changeDescription={itemData.changeDescription}
                // --- MODIFICATION: Passing the unique texture path prop ---
                textureImagePath={itemData.textureImagePath}
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CostOverview;
